import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { type ActionFunctionArgs } from '@remix-run/node'
import { Form, Link, json, redirect, useActionData } from '@remix-run/react'
import { z } from 'zod'
import { ErrorList, Field } from '#app/components/forms'
import { Button } from '#app/components/ui/button'
import { Input } from '#app/components/ui/input'
import { StatusButton } from '#app/components/ui/status-button'
import { prisma } from '#app/utils/db.server'
import { useIsPending } from '#app/utils/misc'

const JoinBingoForm = z.object({
	joinCode: z
		.string()
		.length(6)
		.transform(v => v.toUpperCase()),
})

export async function action({ request }: ActionFunctionArgs) {
	// For now we are just using the database id as the join code but it should be
	// shorter for UX reasons. Still setting it up with an action so I don't need to change the uI

	const formData = await request.formData()

	const submission = await parse(formData, {
		async: true,
		schema: JoinBingoForm.superRefine(async ({ joinCode }, ctx) => {
			const bingoCard = await prisma.bingoCard.findUnique({
				where: { gameCode: joinCode },
				select: { id: true },
			})
			if (!bingoCard) {
				ctx.addIssue({
					path: ['joinCode'],
					code: z.ZodIssueCode.custom,
					message: 'Bingo code does not exist',
				})
			}
		}),
	})

	if (!submission.value) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}

	return redirect(`/bingo/${submission.value.joinCode}`)
}

export default function JoinRoute() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getFieldsetConstraint(JoinBingoForm),
		defaultValue: {},
		lastSubmission: actionData?.submission,
		onValidate({ formData }) {
			return parse(formData, { schema: JoinBingoForm })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex h-full flex-col items-center justify-center gap-10">
			<h1 className="animate-slide-top text-3xl font-medium text-foreground [animation-delay:0.3s] [animation-fill-mode:backwards] lg:text-4xl xl:animate-slide-left xl:[animation-delay:0.4s] xl:[animation-fill-mode:backwards]">
				music <b>bingo </b>
			</h1>
			<Form
				method="POST"
				{...form.props}
				className="flex w-full max-w-xs animate-roll-reveal flex-col rounded-md border border-secondary-foreground/20 bg-secondary p-8 shadow-md [animation-fill-mode:backwards]"
			>
				<Field
					labelProps={{}}
					inputProps={{
						...conform.input(fields.joinCode, { type: 'text' }),
						placeholder: 'Enter bingo code',
						className: 'uppercase placeholder:normal-case',
					}}
					errors={fields.joinCode.errors}
				/>

				<StatusButton
					type="submit"
					status={isPending ? 'pending' : actionData?.status ?? 'idle'}
				>
					Start
				</StatusButton>
			</Form>
			<ErrorList id={form.errorId} errors={form.errors} />
		</div>
	)
}
